import React, {useContext, useEffect, useMemo, useState} from "react"
import {useApolloClient, useMutation, useQuery} from "@apollo/client"

import providerListQuery from "graphql/Storage/Provider/query/providerList"
import showListQuery from "graphql/Media/Show/query/showList"
import movieListQuery from "graphql/Media/Movie/query/movieList"
import ingestionSourceListQuery from "graphql/Storage/Ingestion/query/ingestionSourceList"
import sourceAssetListQuery from "graphql/Storage/Ingestion/query/sourceAssetList"
import ingestionSourceAddMutation from "graphql/Storage/Ingestion/mutation/ingestionSourceAdd"
import ingestionSourceEditMutation from "graphql/Storage/Ingestion/mutation/ingestionSourceEdit"
import ingestionSourceScanMutation from "graphql/Storage/Ingestion/mutation/ingestionSourceScan"
import sourceAssetApproveMutation from "graphql/Storage/Ingestion/mutation/sourceAssetApprove"
import sourceAssetIgnoreMutation from "graphql/Storage/Ingestion/mutation/sourceAssetIgnore"
import sourceAssetRetryMutation from "graphql/Storage/Ingestion/mutation/sourceAssetRetry"
import mediaUpgradePrepareMutation from "graphql/Media/Revision/mutation/mediaUpgradePrepare"
import mediaUpgradeConfirmMutation from "graphql/Media/Revision/mutation/mediaUpgradeConfirm"
import mediaUpgradeCancelMutation from "graphql/Media/Revision/mutation/mediaUpgradeCancel"
import movieUpgradeTargetQuery from "graphql/Media/Movie/query/movieUpgradeTarget"
import episodeUpgradeTargetQuery from "graphql/Media/Episode/query/episodeUpgradeTarget"
import {alertContext} from "context/Alert/alert"
import {FolderPicker} from "./FolderPicker"
import {BackofficePage, BackofficePanel, BackofficeEmptyState} from "components/backoffice/Layout/BackofficePage"

import "./style.scss"

const DEFAULT_EXTENSIONS = "mkv,mp4,avi,mov,m4v"

const sourceToInput = (source: any) => ({
  name: source.name,
  providerId: source.providerId,
  destinationProviderId: source.destinationProviderId,
  rootPath: source.rootPath,
  rootFolderId: source.rootFolderId,
  enabled: source.enabled,
  autoImport: source.autoImport,
  scanIntervalSeconds: source.scanIntervalSeconds,
  stabilitySeconds: source.stabilitySeconds,
  extensions: source.extensions,
})

const formatDate = (timestamp: number) => timestamp
  ? new Date(timestamp * 1000).toLocaleString()
  : "Jamais"

const formatSize = (size: number) => {
  if (!size) return "0 octet"
  const units = ["o", "Ko", "Mo", "Go", "To"]
  const index = Math.min(Math.floor(Math.log(size) / Math.log(1024)), units.length - 1)
  return `${(size / Math.pow(1024, index)).toFixed(index > 1 ? 1 : 0)} ${units[index]}`
}

const statusLabel: Record<string, string> = {
  DETECTED: "Détecté",
  WAITING_STABLE: "En attente de stabilité",
  NEEDS_REVIEW: "À valider",
  IMPORTING: "Import en cours",
  CLEANING: "Suppression de l’INBOX",
  CLEANUP_FAILED: "Suppression en erreur",
  DONE: "Terminé",
  FAILED: "En erreur",
  IGNORED: "Ignoré",
}

const statusClass: Record<string, string> = {
  DETECTED: "secondary",
  WAITING_STABLE: "info",
  NEEDS_REVIEW: "warning",
  IMPORTING: "primary",
  CLEANING: "info",
  CLEANUP_FAILED: "danger",
  DONE: "success",
  FAILED: "danger",
  IGNORED: "dark",
}

const AssetReview = ({asset, sources, providers, shows, movies, refetch}: any) => {
  const source = sources.find((item: any) => item.id === asset.sourceId)
  const episodes = shows.flatMap((show: any) => show.Season?.flatMap((season: any) => season.Episode?.map((episode: any) => ({
    ...episode,
    showName: show.name,
    seasonNumber: season.number,
  })) || []) || [])
  const matchedMovie = movies.find((movie: any) => movie.id === asset.matchedMovieId)
  const matchedEpisode = episodes.find((episode: any) => episode.id === asset.matchedEpisodeId)
  const suggestedTarget = matchedMovie?.isViewable
    ? `UPGRADE_MOVIE:${matchedMovie.id}`
    : matchedMovie ? `MOVIE:${matchedMovie.id}`
      : matchedEpisode?.isViewable
        ? `UPGRADE_EPISODE:${matchedEpisode.id}`
        : matchedEpisode ? `EPISODE:${matchedEpisode.id}` : ""
  const [target, setTarget] = useState(suggestedTarget)
  const [destinationProviderId, setDestinationProviderId] = useState(source?.destinationProviderId || "")
  const [upgradeDraft, setUpgradeDraft] = useState<any>(null)
  const [allowRegression, setAllowRegression] = useState(false)
  const [approve, {loading: approving}] = useMutation(sourceAssetApproveMutation)
  const [prepareUpgrade, {loading: analyzing}] = useMutation(mediaUpgradePrepareMutation)
  const [confirmUpgrade, {loading: confirming}] = useMutation(mediaUpgradeConfirmMutation)
  const [cancelUpgrade, {loading: cancelling}] = useMutation(mediaUpgradeCancelMutation)
  const [ignore, {loading: ignoring}] = useMutation(sourceAssetIgnoreMutation)
  const apolloClient = useApolloClient()
  const {setError, setMessage} = useContext(alertContext)

  useEffect(() => {
    setTarget(suggestedTarget)
    setDestinationProviderId(source?.destinationProviderId || "")
    setUpgradeDraft(null)
    setAllowRegression(false)
  }, [asset.id, suggestedTarget, source?.destinationProviderId])

  const submit = async () => {
    const [type, id] = target.split(":")
    if (!id) return
    try {
      if (type === "UPGRADE_MOVIE" || type === "UPGRADE_EPISODE") {
        const targetResponse = await apolloClient.query({
          query: type === "UPGRADE_MOVIE" ? movieUpgradeTargetQuery : episodeUpgradeTargetQuery,
          variables: {id},
          fetchPolicy: "network-only",
        })
        const mediaId = type === "UPGRADE_MOVIE"
          ? targetResponse.data?.movie?.Media?.id
          : targetResponse.data?.episode?.Media?.id
        if (!mediaId) throw new Error("Aucun média publié n’a été trouvé pour cette sélection")
        const response = await prepareUpgrade({variables: {
          mediaId,
          sourceFileId: asset.fileId,
          destinationProviderId,
        }})
        setUpgradeDraft(response.data?.revision)
        return
      }
      await approve({variables: {
        id: asset.id,
        input: {
          Episode: type === "EPISODE" ? id : null,
          Movie: type === "MOVIE" ? id : null,
          destinationProviderId,
        },
      }})
      setMessage(<span>Import ajouté à la file.</span>)
      await refetch()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const confirmExistingMediaUpgrade = async () => {
    try {
      await confirmUpgrade({variables: {revisionId: upgradeDraft.id, allowRegression}})
      setMessage(<span>Remplacement ajouté à la file. La version actuelle reste active jusqu’à validation.</span>)
      await refetch()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const cancelExistingMediaUpgrade = async () => {
    try {
      await cancelUpgrade({variables: {revisionId: upgradeDraft.id}})
      setUpgradeDraft(null)
      setAllowRegression(false)
      await refetch()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const ignoreAsset = async () => {
    try {
      await ignore({variables: {id: asset.id}})
      await refetch()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const isUpgrade = target.startsWith("UPGRADE_")
  const comparison = upgradeDraft?.comparison
  const regressions = comparison?.regressions || []
  const pendingMovies = movies.filter((movie: any) => !movie.isViewable)
  const existingMovies = movies.filter((movie: any) => movie.isViewable)
  const pendingEpisodes = episodes.filter((episode: any) => !episode.isViewable)
  const existingEpisodes = episodes.filter((episode: any) => episode.isViewable)

  return <div className="mt-3">
    {!upgradeDraft && <div className="form-row">
      <div className="col-md-7 mb-2">
        <select className="form-control" value={target} onChange={event => setTarget(event.target.value)}>
          <option value="">Associer à un média…</option>
          <optgroup label="Films à importer">
            {pendingMovies.map((movie: any) =>
              <option key={movie.id} value={`MOVIE:${movie.id}`}>{movie.name}</option>
            )}
          </optgroup>
          <optgroup label="Films existants — améliorer la qualité">
            {existingMovies.map((movie: any) =>
              <option key={`upgrade-movie-${movie.id}`} value={`UPGRADE_MOVIE:${movie.id}`}>{movie.name} — remplacer la version</option>
            )}
          </optgroup>
          <optgroup label="Épisodes à importer">
            {pendingEpisodes.map((episode: any) => <option key={episode.id} value={`EPISODE:${episode.id}`}>
              {episode.showName} · S{String(episode.seasonNumber).padStart(2, "0")}E{String(episode.number).padStart(2, "0")} — {episode.name}
            </option>)}
          </optgroup>
          <optgroup label="Épisodes existants — améliorer la qualité">
            {existingEpisodes.map((episode: any) => <option key={`upgrade-episode-${episode.id}`} value={`UPGRADE_EPISODE:${episode.id}`}>
              {episode.showName} · S{String(episode.seasonNumber).padStart(2, "0")}E{String(episode.number).padStart(2, "0")} — remplacer la version
            </option>)}
          </optgroup>
        </select>
      </div>
      <div className="col-md-5 mb-2">
        <select className="form-control" value={destinationProviderId} onChange={event => setDestinationProviderId(event.target.value)}>
          <option value="">Destination…</option>
          {providers.map((provider: any) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </select>
      </div>
    </div>}
    {!upgradeDraft && <>
      <button className="btn btn-dark btn-sm mr-2" disabled={!target || !destinationProviderId || approving || analyzing} onClick={submit}>
        {analyzing ? "Analyse…" : approving ? "Validation…" : isUpgrade ? "Analyser avant remplacement" : "Valider et importer"}
      </button>
      <button className="btn btn-outline-secondary btn-sm" disabled={ignoring} onClick={ignoreAsset}>Ignorer</button>
    </>}
    {upgradeDraft?.status === "WAITING_CONFIRMATION" && comparison && <div className="mt-3">
      <div className={`alert ${comparison.verdict === "PROBABLE_UPGRADE" ? "alert-success" : comparison.verdict === "REGRESSION_WARNING" ? "alert-danger" : "alert-warning"}`}>
        <strong>{comparison.verdict === "PROBABLE_UPGRADE" ? "Amélioration probable" : comparison.verdict === "REGRESSION_WARNING" ? "Régressions détectées" : "Comparaison incertaine"}</strong>
        <div className="small mt-1">
          Actuel : {comparison.current?.video?.width || "?"}×{comparison.current?.video?.height || "?"} {comparison.current?.video?.codec || ""}
          {" → "}Nouveau : {comparison.candidate?.video?.width || "?"}×{comparison.candidate?.video?.height || "?"} {comparison.candidate?.video?.codec || ""}
        </div>
      </div>
      {comparison.improvements?.length > 0 && <div className="small text-success mb-1">Améliorations : {comparison.improvements.join(", ")}</div>}
      {regressions.length > 0 && <div className="small text-danger mb-1">Régressions : {regressions.join(", ")}</div>}
      {comparison.warnings?.length > 0 && <div className="small text-warning mb-2">À vérifier : {comparison.warnings.join(", ")}</div>}
      {regressions.length > 0 && <div className="form-check mb-2">
        <input id={`allow-regression-${asset.id}`} className="form-check-input" type="checkbox" checked={allowRegression} onChange={event => setAllowRegression(event.target.checked)}/>
        <label className="form-check-label" htmlFor={`allow-regression-${asset.id}`}>Confirmer malgré les régressions</label>
      </div>}
      <button className="btn btn-success btn-sm mr-2" disabled={confirming || (regressions.length > 0 && !allowRegression)} onClick={confirmExistingMediaUpgrade}>
        {confirming ? "Démarrage…" : "Confirmer le remplacement"}
      </button>
      <button className="btn btn-outline-secondary btn-sm" disabled={cancelling} onClick={cancelExistingMediaUpgrade}>Annuler</button>
    </div>}
  </div>
}

export const IngestionInbox = () => {
  const {setError, setMessage} = useContext(alertContext)
  const {data: providerData} = useQuery(providerListQuery)
  const {data: showData} = useQuery(showListQuery)
  const {data: movieData} = useQuery(movieListQuery)
  const {data: sourceData, refetch: refetchSources} = useQuery(ingestionSourceListQuery, {fetchPolicy: "cache-and-network"})
  const {data: assetData, refetch: refetchAssets} = useQuery(sourceAssetListQuery, {
    fetchPolicy: "cache-and-network",
    pollInterval: 10000,
  })
  const [addSource, {loading: adding}] = useMutation(ingestionSourceAddMutation)
  const [editSource] = useMutation(ingestionSourceEditMutation)
  const [scanSource] = useMutation(ingestionSourceScanMutation)
  const [retryAsset] = useMutation(sourceAssetRetryMutation)

  const providers = providerData?.providerList || []
  const sources = sourceData?.ingestionSourceList || []
  const assets = assetData?.sourceAssetList || []
  const shows = showData?.showList || []
  const movies = movieData?.movieList || []
  const sourceProviders = providers.filter((provider: any) => provider.target === "SOURCE")
  const destinationProviders = providers.filter((provider: any) => provider.target === "MEDIA")

  const [form, setForm] = useState<any>({
    name: "",
    providerId: "",
    destinationProviderId: "",
    rootPath: "/",
    rootFolderId: null,
    enabled: true,
    autoImport: false,
    scanIntervalSeconds: 300,
    stabilitySeconds: 60,
    extensions: DEFAULT_EXTENSIONS,
  })

  useEffect(() => {
    setForm((current: any) => ({
      ...current,
      providerId: current.providerId || sourceProviders[0]?.id || providers[0]?.id || "",
      destinationProviderId: current.destinationProviderId || destinationProviders[0]?.id || providers[0]?.id || "",
    }))
  }, [providers.length])

  const counts = useMemo(() => assets.reduce((result: any, asset: any) => ({
    ...result,
    [asset.status]: (result[asset.status] || 0) + 1,
  }), {}), [assets])

  const createSource = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      await addSource({variables: {input: {
        ...form,
        scanIntervalSeconds: Number(form.scanIntervalSeconds),
        stabilitySeconds: Number(form.stabilitySeconds),
        extensions: form.extensions.split(",").map((item: string) => item.trim()).filter(Boolean),
      }}})
      setForm({...form, name: "", rootPath: "/", rootFolderId: null})
      setMessage(<span>Source INBOX créée.</span>)
      await refetchSources()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const toggleSource = async (source: any) => {
    try {
      await editSource({variables: {id: source.id, input: {...sourceToInput(source), enabled: !source.enabled}}})
      await refetchSources()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const runScan = async (source: any) => {
    try {
      await scanSource({variables: {id: source.id}})
      setMessage(<span>Scan terminé.</span>)
      await Promise.all([refetchSources(), refetchAssets()])
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  const retry = async (asset: any) => {
    try {
      await retryAsset({variables: {id: asset.id}})
      await refetchAssets()
    } catch (error) {
      setError(<span>{error.message}</span>)
    }
  }

  return <BackofficePage
    className="ingestion-inbox-container"
    eyebrow="Automatisation"
    title="Inbox média"
    description="Surveillez les dossiers sources, validez les correspondances et suivez les imports automatiques."
    icon="fa-inbox"
  >
    <section className="ingestion-stats" aria-label="Résumé des fichiers détectés">
      {Object.entries(statusLabel).map(([status, label]) => <div className={`ingestion-stat stat-${status.toLowerCase()}`} key={status}>
        <span className="stat-marker"></span>
        <div><strong>{counts[status] || 0}</strong><small>{label}</small></div>
      </div>)}
    </section>

    <BackofficePanel title="Ajouter une source" description="Configurez un dossier à surveiller automatiquement." icon="fa-folder-plus">
        <form onSubmit={createSource}>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Nom</label>
              <input className="form-control" required value={form.name} onChange={event => setForm({...form, name: event.target.value})}/>
            </div>
            <div className="form-group col-md-4">
              <label>Provider source</label>
              <select className="form-control" required value={form.providerId} onChange={event => setForm({...form, providerId: event.target.value, rootFolderId: null, rootPath: "/"})}>
                {(sourceProviders.length ? sourceProviders : providers).map((provider: any) => <option value={provider.id} key={provider.id}>{provider.name}</option>)}
              </select>
            </div>
            <div className="form-group col-md-4">
              <label>Provider de destination</label>
              <select className="form-control" required value={form.destinationProviderId} onChange={event => setForm({...form, destinationProviderId: event.target.value})}>
                {(destinationProviders.length ? destinationProviders : providers).map((provider: any) => <option value={provider.id} key={provider.id}>{provider.name}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group col-md-4">
              <label>Dossier racine relatif</label>
              <FolderPicker
                providerId={form.providerId}
                value={{id: form.rootFolderId, path: form.rootPath}}
                onChange={folder => setForm({...form, rootFolderId: folder.id, rootPath: folder.path})}
              />
              <small className="form-text text-muted">Le sélecteur affiche uniquement les dossiers accessibles au provider.</small>
            </div>
            <div className="form-group col-md-4">
              <label>Extensions</label>
              <input className="form-control" value={form.extensions} onChange={event => setForm({...form, extensions: event.target.value})}/>
            </div>
            <div className="form-group col-6 col-md-2">
              <label>Scan (secondes)</label>
              <input type="number" min="15" className="form-control" value={form.scanIntervalSeconds} onChange={event => setForm({...form, scanIntervalSeconds: event.target.value})}/>
            </div>
            <div className="form-group col-6 col-md-2">
              <label>Stabilité (secondes)</label>
              <input type="number" min="0" className="form-control" value={form.stabilitySeconds} onChange={event => setForm({...form, stabilitySeconds: event.target.value})}/>
            </div>
          </div>
          <div className="form-check mb-2">
            <input id="autoImport" type="checkbox" className="form-check-input" checked={form.autoImport} onChange={event => setForm({...form, autoImport: event.target.checked})}/>
            <label className="form-check-label" htmlFor="autoImport">Importer automatiquement les correspondances certaines</label>
          </div>
          <button className="bo-primary-action" disabled={adding || !providers.length}>{adding ? <span className="icon-spinner"></span> : <span className="fas fa-plus"></span>}{adding ? "Création…" : "Créer la source"}</button>
        </form>
    </BackofficePanel>

    <section className="ingestion-section">
      <header className="ingestion-section-heading"><div><h2>Sources surveillées</h2><p>{sources.length} source{sources.length > 1 ? "s" : ""} configurée{sources.length > 1 ? "s" : ""}</p></div></header>
    {sources.length === 0 && <BackofficeEmptyState title="Aucune source configurée" description="Ajoutez un dossier à surveiller pour détecter automatiquement les nouveaux médias." icon="fa-folder-open" />}
    {sources.map((source: any) => <div className="card ingestion-source-card" key={source.id}>
      <div className="card-body">
        <div className="d-flex justify-content-between flex-wrap">
          <div>
            <strong>{source.name}</strong> <span className={`badge badge-${source.enabled ? "success" : "secondary"}`}>{source.enabled ? "Active" : "Pause"}</span>
            {source.autoImport && <span className="badge badge-primary ml-1">Auto</span>}
            <div className="text-muted small">{source.rootPath} · dernier scan : {formatDate(source.lastScanAt)}</div>
            {source.lastScanError && <div className="text-danger small">{source.lastScanError}</div>}
          </div>
          <div className="mt-2 mt-md-0">
            <button className="btn btn-outline-dark btn-sm mr-2" onClick={() => runScan(source)}><span className="fas fa-sync"></span>Scanner</button>
            <button className="btn btn-outline-secondary btn-sm" onClick={() => toggleSource(source)}>{source.enabled ? "Mettre en pause" : "Activer"}</button>
          </div>
        </div>
      </div>
    </div>)}
    </section>

    <section className="ingestion-section">
    <header className="ingestion-section-heading"><div><h2>Fichiers détectés</h2><p>{assets.length} fichier{assets.length > 1 ? "s" : ""} dans l'inbox</p></div></header>
    {assets.length === 0 && <BackofficeEmptyState title="Aucun fichier détecté" description="Les nouveaux fichiers apparaîtront ici après le prochain scan." icon="fa-file-video" />}
    {assets.map((asset: any) => {
      const source = sources.find((item: any) => item.id === asset.sourceId)
      const candidate = asset.candidate
      return <div className="card ingestion-asset-card" key={asset.id}>
        <div className="card-body">
          <div className="d-flex justify-content-between flex-wrap">
            <div>
              <strong>{asset.name}</strong>
              <div className="small text-muted">{source?.name || asset.sourceId} · {asset.remotePath} · {formatSize(asset.size)}</div>
              {candidate && <div className="small mt-1">
                Proposition : <strong>{candidate.title || "Titre inconnu"}</strong>
                {candidate.year ? ` (${candidate.year})` : ""}
                {candidate.type === "EPISODE" ? ` · S${candidate.season}E${candidate.episode}` : ""}
                {asset.matchConfidence ? ` · confiance ${asset.matchConfidence}%` : ""}
              </div>}
              {asset.error && <div className="text-danger small mt-1">{asset.error}</div>}
            </div>
            <div><span className={`badge badge-${statusClass[asset.status] || "secondary"}`}>{statusLabel[asset.status] || asset.status}</span></div>
          </div>
          {asset.status === "NEEDS_REVIEW" && <AssetReview
            asset={asset} sources={sources} providers={destinationProviders.length ? destinationProviders : providers}
            shows={shows} movies={movies} refetch={refetchAssets}
          />}
          {["FAILED", "CLEANUP_FAILED"].includes(asset.status) && <button className="btn btn-outline-danger btn-sm mt-3" onClick={() => retry(asset)}>
            {asset.status === "CLEANUP_FAILED" ? "Relancer uniquement la suppression" : "Relancer"}
          </button>}
        </div>
      </div>
    })}
    </section>
  </BackofficePage>
}
